import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import apiService from '../../services/apiService';
import Dialogo from '../../components/Dialogo';
import '../MetasConfig.css';
import './Escalas.css';
import {
    EFFECTS, EFFECT_LABEL, KINDS, KIND_LABEL, STATUSES, STATUS_LABEL, brDate, diaFechado, excecaoParaForm, excecaoParaPayload,
    excecaoTocaFechado, excecaoVazia, exigirForma, mensagemErro, motivoValido, resumoHorario, validarExcecao,
} from './escalasUtil';

const anoAtual = () => new Date().getFullYear();

/**
 * Aba Exceções: o que muda a escala em dias específicos. Três níveis, do mais forte ao mais fraco:
 * vendedor (falta, folga, férias, reposição), loja (fechada, horário especial) e nacional (feriado).
 * Vence o nível mais específico que cobre o dia. Nacional vale para TODAS as empresas e fica numa
 * subseção própria, porque a API restringe quem edita fora de QA.
 */
function ExcecoesTab({ subdomain, lookups, notify }) {
    const { stores, salesmen, nomeLoja } = lookups;

    // ------------------------------------------------------------------ loja e vendedor
    const [lista, setLista] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filtro, setFiltro] = useState({ level: '', store_id: '', salesman_id: '', date_from: '', date_to: '' });
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);
    const [erroApi, setErroApi] = useState('');
    const [paraExcluir, setParaExcluir] = useState(null);
    const [motivoExcluir, setMotivoExcluir] = useState('');   // motivo da remoção (obrigatório se a exceção toca dia fechado)
    const [primeiroAberto, setPrimeiroAberto] = useState(null);   // first_open_day da API: dia anterior a ele é fechado
    const [resumo, setResumo] = useState(null);       // { payload, dados, exigeAutorDoPassado } do dry_run
    const [filtroLoteLoja, setFiltroLoteLoja] = useState('');
    const [buscaLote, setBuscaLote] = useState('');
    const savingRef = useRef(false);

    const carregar = useCallback(async () => {
        if (!subdomain) return;
        setLoading(true);
        try {
            const corpo = { subdomain };
            if (filtro.level) corpo.level = filtro.level;
            if (filtro.store_id) corpo.store_id = Number(filtro.store_id);
            if (filtro.salesman_id) corpo.salesman_id = Number(filtro.salesman_id);
            if (filtro.date_from) corpo.date_from = filtro.date_from;
            if (filtro.date_to) corpo.date_to = filtro.date_to;
            const r = exigirForma(await apiService.scheduleListExceptions(corpo), 'exceptions');
            setLista(r.exceptions);
            setPrimeiroAberto(r.first_open_day || null);
        } catch (e) {
            notify(mensagemErro(e, 'Erro ao carregar as exceções'), 'error');
        } finally {
            setLoading(false);
        }
    }, [subdomain, filtro, notify]);

    useEffect(() => { setForm(null); setLista([]); }, [subdomain]);
    useEffect(() => { carregar(); }, [carregar]);

    const erro = form ? validarExcecao(form) : null;
    const tocaFechado = form ? excecaoTocaFechado(form, primeiroAberto) : false;
    const setCampo = (k, v) => setForm((p) => ({ ...p, [k]: v }));
    const abrirNova = () => { setErroApi(''); setFiltroLoteLoja(''); setBuscaLote(''); setForm(excecaoVazia()); };
    const abrirEdicao = (e) => { setErroApi(''); setForm(excecaoParaForm(e)); };
    const fechar = () => { setForm(null); setErroApi(''); };

    const lote = !!(form && form.level === 'salesman' && !form.id && form.salesman_ids.length > 1);

    const vendedoresVisiveis = useMemo(() => {
        const q = buscaLote.trim().toLowerCase();
        return salesmen.filter((s) => (!filtroLoteLoja || String(s.store_id) === String(filtroLoteLoja))
            && (!q || String(s.salesman_name || '').toLowerCase().includes(q)));
    }, [salesmen, filtroLoteLoja, buscaLote]);

    const alternarVendedor = (id) => setForm((p) => {
        if (p.id) return { ...p, salesman_ids: [id] };
        const tem = p.salesman_ids.includes(id);
        return { ...p, salesman_ids: tem ? p.salesman_ids.filter((x) => x !== id) : [...p.salesman_ids, id] };
    });
    const marcarTodosVisiveis = () => setForm((p) => ({
        ...p, salesman_ids: [...new Set([...p.salesman_ids, ...vendedoresVisiveis.map((s) => s.salesman_id)])],
    }));
    const limparSelecao = () => setCampo('salesman_ids', []);

    const gravar = async (payload) => {
        const r = await apiService.scheduleSaveException(payload);
        if (!r || r.success !== true) throw new Error('resposta fora de forma');
        return r;
    };

    const salvar = async () => {
        if (erro) { notify(erro, 'error'); return; }
        if (savingRef.current) return;
        savingRef.current = true;
        setSaving(true);
        setErroApi('');
        try {
            const r = await gravar(excecaoParaPayload(form, subdomain, false));
            const rev = Number(r.revisions_created) > 0 ? ` (${r.revisions_created} revisão(ões) do passado gravada(s))` : '';
            notify(`${form.id ? 'Exceção atualizada' : 'Exceção criada'}${rev}`, 'success');
            fechar();
            carregar();
        } catch (e) {
            setErroApi(mensagemErro(e, 'Erro ao salvar a exceção'));
        } finally {
            savingRef.current = false;
            setSaving(false);
        }
    };

    // Lote: primeiro o dry_run mostra o que seria criado; só depois do "Criar" nada é gravado de fato.
    const preVisualizar = async () => {
        if (erro) { notify(erro, 'error'); return; }
        if (savingRef.current) return;
        savingRef.current = true;
        setSaving(true);
        setErroApi('');
        try {
            const payload = excecaoParaPayload(form, subdomain, true);
            const r = await gravar(payload);
            if (!Array.isArray(r.would_create)) throw new Error('resposta fora de forma');
            setResumo({ payload, dados: r.would_create, exigeAutorDoPassado: !!r.requires_past_author });
        } catch (e) {
            setErroApi(mensagemErro(e, 'Erro ao simular a criação'));
        } finally {
            savingRef.current = false;
            setSaving(false);
        }
    };

    const confirmarLote = async () => {
        if (savingRef.current) return;
        savingRef.current = true;
        setSaving(true);
        try {
            const { dry_run: _ignorado, ...real } = resumo.payload;
            const r = await gravar(real);
            const n = (r.exceptions || []).length;
            const rev = Number(r.revisions_created) > 0 ? `; ${r.revisions_created} revisão(ões) do passado gravada(s)` : '';
            notify(`${n} exceç${n === 1 ? 'ão criada' : 'ões criadas'}${rev}`, 'success');
            setResumo(null);
            fechar();
            carregar();
        } catch (e) {
            setResumo(null);
            setErroApi(mensagemErro(e, 'Erro ao criar as exceções'));
        } finally {
            savingRef.current = false;
            setSaving(false);
        }
    };

    const pedirExclusao = (e) => { setMotivoExcluir(''); setParaExcluir(e); };
    const exclusaoTocaFechado = !!(paraExcluir && (paraExcluir.touches_closed_days || diaFechado(paraExcluir.date_from, primeiroAberto)));
    const excluir = async () => {
        const x = paraExcluir;
        if (exclusaoTocaFechado && !motivoValido(motivoExcluir)) return;
        try {
            const r = await apiService.scheduleDeleteException({ subdomain, id: x.id, reason: motivoExcluir.trim() || undefined });
            const rev = r && Number(r.revisions_created) > 0 ? ` (${r.revisions_created} revisão(ões) do passado gravada(s))` : '';
            notify(`Exceção removida${rev}`, 'success');
            setParaExcluir(null);
            carregar();
        } catch (e) {
            notify(mensagemErro(e, 'Erro ao remover a exceção'), 'error');
            setParaExcluir(null);
        }
    };

    const alvoTexto = (e) => (e.level === 'store'
        ? (e.store_name || nomeLoja(e.store_id))
        : (e.salesman_name || `Vendedor ${e.salesman_id}`));
    const horarioTexto = (e) => (e.effect === 'off' ? '—' : (e.start_time ? resumoHorario(e.start_time, e.end_time) : 'da escala'));
    const totalSemOverlap = resumo ? resumo.dados.filter((d) => (d.overlaps || []).length === 0).length : 0;

    // ------------------------------------------------------------------ nacional
    const [ano, setAno] = useState(anoAtual());
    const [nacionais, setNacionais] = useState([]);
    const [carregandoNac, setCarregandoNac] = useState(false);
    const [formNac, setFormNac] = useState(null);
    const [erroNac, setErroNac] = useState('');
    const [seed, setSeed] = useState(null);           // { ano, dados }
    const [ocupadoNac, setOcupadoNac] = useState(false);
    const [nacParaExcluir, setNacParaExcluir] = useState(null);
    const [primeiroAbertoNac, setPrimeiroAbertoNac] = useState(null);

    const carregarNac = useCallback(async () => {
        setCarregandoNac(true);
        try {
            const r = exigirForma(await apiService.scheduleListNational({ year: Number(ano) }), 'national');
            setNacionais(r.national);
            setPrimeiroAbertoNac(r.first_open_day || null);
        } catch (e) {
            notify(mensagemErro(e, 'Erro ao carregar os feriados nacionais'), 'error');
        } finally {
            setCarregandoNac(false);
        }
    }, [ano, notify]);

    useEffect(() => { carregarNac(); }, [carregarNac]);

    const novoNac = () => {
        setErroNac('');
        setFormNac({ id: null, date: `${ano}-01-01`, kind: 'holiday', effect: 'off', label: '', region: '', active: true });
    };
    const editarNac = (n) => {
        setErroNac('');
        setFormNac({ id: n.id, date: String(n.date).split('T')[0], kind: n.kind, effect: n.effect, label: n.label, region: n.region || '', active: !!n.active });
    };
    const erroFormNac = formNac && (!formNac.date ? 'Informe a data'
        : (diaFechado(formNac.date, primeiroAbertoNac)
            ? `Data fechada é imutável: só se cadastra feriado a partir de ${brDate(primeiroAbertoNac)}. Para corrigir o passado de uma empresa, use uma exceção com motivo`
            : (!formNac.label.trim() ? 'Informe o nome do feriado' : null)));

    const salvarNac = async () => {
        if (erroFormNac) { notify(erroFormNac, 'error'); return; }
        setOcupadoNac(true);
        setErroNac('');
        try {
            const r = await apiService.scheduleSaveNational({
                id: formNac.id || undefined, date: formNac.date, kind: formNac.kind, effect: formNac.effect,
                label: formNac.label.trim(), region: formNac.region.trim() || null, active: formNac.active,
            });
            if (!r || r.success !== true) throw new Error('resposta fora de forma');
            notify('Feriado salvo', 'success');
            setFormNac(null);
            carregarNac();
        } catch (e) {
            // 403 fora de QA para quem não está em SCHEDULE_NATIONAL_ADMIN_IDS: a mensagem da API explica.
            setErroNac(mensagemErro(e, 'Erro ao salvar o feriado'));
        } finally {
            setOcupadoNac(false);
        }
    };

    const excluirNac = async () => {
        const n = nacParaExcluir;
        try {
            await apiService.scheduleDeleteNational({ id: n.id });
            notify('Feriado removido', 'success');
            setNacParaExcluir(null);
            carregarNac();
        } catch (e) {
            setNacParaExcluir(null);
            setErroNac(mensagemErro(e, 'Erro ao remover o feriado'));
        }
    };

    const simularSeed = async () => {
        setOcupadoNac(true);
        setErroNac('');
        try {
            const r = await apiService.scheduleSeedNational({ year: Number(ano), dry_run: true });
            if (!r || r.success !== true || !Array.isArray(r.created)) throw new Error('resposta fora de forma');
            setSeed({ ano: Number(ano), dados: r });
        } catch (e) {
            setErroNac(mensagemErro(e, 'Erro ao simular a semeadura'));
        } finally {
            setOcupadoNac(false);
        }
    };

    const aplicarSeed = async () => {
        setOcupadoNac(true);
        try {
            const r = await apiService.scheduleSeedNational({ year: seed.ano, dry_run: false });
            if (!r || r.success !== true) throw new Error('resposta fora de forma');
            notify(`${(r.created || []).length} feriado(s) semeado(s) em ${seed.ano}`, 'success');
            setSeed(null);
            carregarNac();
        } catch (e) {
            setSeed(null);
            setErroNac(mensagemErro(e, 'Erro ao semear os feriados'));
        } finally {
            setOcupadoNac(false);
        }
    };

    return (
        <div className="mc-page">
            <p className="mc-nota">
                Exceção muda a escala em dias específicos. Vence o nível mais específico:
                <strong> vendedor</strong> (falta, folga, férias, reposição) sobre <strong>loja</strong> (fechada,
                horário especial) sobre <strong>nacional</strong> (feriado). Férias futuras entram como “Planejada”.
                O <strong>motivo é obrigatório</strong>: é ele que explica a exceção depois. Dia já fechado só muda por revisão.
            </p>

            <div className="mc-filters">
                <div className="mc-field">
                    <label htmlFor="esc-ex-f-nivel">Nível</label>
                    <select id="esc-ex-f-nivel" value={filtro.level} onChange={(e) => setFiltro((p) => ({ ...p, level: e.target.value }))}>
                        <option value="">Todos</option>
                        <option value="salesman">Vendedor</option>
                        <option value="store">Loja</option>
                    </select>
                </div>
                <div className="mc-field">
                    <label htmlFor="esc-ex-f-loja">Loja</label>
                    <select id="esc-ex-f-loja" value={filtro.store_id} onChange={(e) => setFiltro((p) => ({ ...p, store_id: e.target.value }))}>
                        <option value="">Todas</option>
                        {stores.map((s) => <option key={s.id} value={s.id}>{s.display_name || s.name}</option>)}
                    </select>
                </div>
                <div className="mc-field">
                    <label htmlFor="esc-ex-f-vend">Vendedor</label>
                    <select id="esc-ex-f-vend" value={filtro.salesman_id} onChange={(e) => setFiltro((p) => ({ ...p, salesman_id: e.target.value }))}>
                        <option value="">Todos</option>
                        {salesmen.map((s) => <option key={s.salesman_id} value={s.salesman_id}>{s.salesman_name} — {s.store_name}</option>)}
                    </select>
                </div>
                <div className="mc-field">
                    <label htmlFor="esc-ex-f-de">De</label>
                    <input id="esc-ex-f-de" type="date" value={filtro.date_from} onChange={(e) => setFiltro((p) => ({ ...p, date_from: e.target.value }))} />
                </div>
                <div className="mc-field">
                    <label htmlFor="esc-ex-f-ate">Até</label>
                    <input id="esc-ex-f-ate" type="date" value={filtro.date_to} onChange={(e) => setFiltro((p) => ({ ...p, date_to: e.target.value }))} />
                </div>
                <div className="mc-field mc-filters-actions esc-acoes">
                    <button className="mc-btn mc-btn-primary" onClick={abrirNova} disabled={!subdomain}>➕ Nova exceção</button>
                </div>
            </div>

            {form && (
                <div className="mc-form-panel">
                    <h2>{form.id ? 'Editar exceção' : 'Nova exceção'}</h2>
                    {tocaFechado && (
                        <p className="esc-aviso" role="note">
                            <strong>Dia fechado:</strong> o período vai antes de {brDate(primeiroAberto)}. Isto vira uma <strong>revisão
                            do passado</strong>: exige motivo, só quem está autorizado a corrigir o passado pode fazê-la, e o estado
                            anterior dos dias fica guardado (a revisão anterior continua lá). No máximo 62 dias e 300 vendedores por vez.
                        </p>
                    )}
                    <div className="mc-form-grid">
                        <div className="mc-field">
                            <label htmlFor="esc-ex-nivel">Nível *</label>
                            <select id="esc-ex-nivel" value={form.level} disabled={!!form.id}
                                onChange={(e) => setForm((p) => ({ ...p, level: e.target.value, store_id: '', salesman_ids: [] }))}>
                                <option value="salesman">Vendedor</option>
                                <option value="store">Loja</option>
                            </select>
                        </div>
                        {form.level === 'store' && (
                            <div className="mc-field">
                                <label htmlFor="esc-ex-loja">Loja *</label>
                                <select id="esc-ex-loja" value={form.store_id} onChange={(e) => setCampo('store_id', e.target.value)}>
                                    <option value="">— Selecione —</option>
                                    {stores.map((s) => <option key={s.id} value={s.id}>{s.display_name || s.name}</option>)}
                                </select>
                            </div>
                        )}
                        <div className="mc-field">
                            <label htmlFor="esc-ex-tipo">Tipo *</label>
                            <select id="esc-ex-tipo" value={form.kind} onChange={(e) => setCampo('kind', e.target.value)}>
                                {KINDS.map((k) => <option key={k.key} value={k.key}>{k.label}</option>)}
                            </select>
                        </div>
                        <div className="mc-field">
                            <label htmlFor="esc-ex-efeito">Efeito *</label>
                            <select id="esc-ex-efeito" value={form.effect}
                                onChange={(e) => setForm((p) => ({ ...p, effect: e.target.value, start_time: e.target.value === 'off' ? '' : p.start_time, end_time: e.target.value === 'off' ? '' : p.end_time }))}>
                                {EFFECTS.map((x) => <option key={x.key} value={x.key}>{x.label}</option>)}
                            </select>
                        </div>
                        <div className="mc-field">
                            <label htmlFor="esc-ex-de">De *</label>
                            <input id="esc-ex-de" type="date" value={form.date_from}
                                onChange={(e) => setForm((p) => ({ ...p, date_from: e.target.value, date_to: p.date_to < e.target.value ? e.target.value : p.date_to }))} />
                        </div>
                        <div className="mc-field">
                            <label htmlFor="esc-ex-ate">Até *</label>
                            <input id="esc-ex-ate" type="date" value={form.date_to} onChange={(e) => setCampo('date_to', e.target.value)} />
                        </div>
                        {form.effect !== 'off' && (
                            <>
                                <div className="mc-field">
                                    <label htmlFor="esc-ex-ini">Início{form.effect === 'override_hours' ? ' *' : ''}</label>
                                    <input id="esc-ex-ini" type="time" value={form.start_time} onChange={(e) => setCampo('start_time', e.target.value)} />
                                </div>
                                <div className="mc-field">
                                    <label htmlFor="esc-ex-fim">Fim{form.effect === 'override_hours' ? ' *' : ''}</label>
                                    <input id="esc-ex-fim" type="time" value={form.end_time} onChange={(e) => setCampo('end_time', e.target.value)} />
                                </div>
                            </>
                        )}
                        <div className="mc-field">
                            <label htmlFor="esc-ex-sit">Situação</label>
                            <select id="esc-ex-sit" value={form.status} onChange={(e) => setCampo('status', e.target.value)}>
                                {STATUSES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                            </select>
                        </div>
                        <div className="mc-field mc-field-wide">
                            <label htmlFor="esc-ex-motivo">Motivo *</label>
                            <input id="esc-ex-motivo" type="text" maxLength={240} value={form.reason} onChange={(e) => setCampo('reason', e.target.value)} />
                        </div>
                        <div className="mc-field">
                            <label><input type="checkbox" checked={form.active} onChange={(e) => setCampo('active', e.target.checked)} /> Ativa</label>
                        </div>
                    </div>

                    {form.level === 'salesman' && (
                        <div className="esc-lote">
                            <h3>
                                {form.id ? 'Vendedor' : 'Vendedores'}
                                {' '}<small>({form.salesman_ids.length} selecionado{form.salesman_ids.length === 1 ? '' : 's'})</small>
                            </h3>
                            {!form.id && (
                                <div className="esc-lote-barra">
                                    <select aria-label="Filtrar por loja" value={filtroLoteLoja} onChange={(e) => setFiltroLoteLoja(e.target.value)}>
                                        <option value="">Todas as lojas</option>
                                        {stores.map((s) => <option key={s.id} value={s.id}>{s.display_name || s.name}</option>)}
                                    </select>
                                    <input type="search" placeholder="Buscar vendedor" aria-label="Buscar vendedor" value={buscaLote} onChange={(e) => setBuscaLote(e.target.value)} />
                                    <button className="mc-btn mc-btn-sm" onClick={marcarTodosVisiveis}>Marcar os {vendedoresVisiveis.length} da lista</button>
                                    <button className="mc-btn mc-btn-sm" onClick={limparSelecao}>Limpar</button>
                                </div>
                            )}
                            <div className="esc-lote-lista">
                                {(form.id ? salesmen.filter((s) => form.salesman_ids.includes(s.salesman_id)) : vendedoresVisiveis).map((s) => (
                                    <label key={s.salesman_id} className="esc-lote-item">
                                        <input type="checkbox" checked={form.salesman_ids.includes(s.salesman_id)} disabled={!!form.id}
                                            onChange={() => alternarVendedor(s.salesman_id)} />
                                        <span>{s.salesman_name}<small> — {s.store_name}{s.is_active ? '' : ' (inativo)'}</small></span>
                                    </label>
                                ))}
                                {!form.id && vendedoresVisiveis.length === 0 && <span className="mc-empty">Nenhum vendedor com esse filtro.</span>}
                            </div>
                            {lote && <p className="esc-dica">Mais de um vendedor: a criação passa por uma pré-visualização antes de gravar.</p>}
                        </div>
                    )}

                    {erro && <p className="mc-nota">{erro}</p>}
                    {erroApi && <p className="esc-erro" role="alert">{erroApi}</p>}
                    <div className="mc-form-actions">
                        <button className="mc-btn" onClick={fechar} disabled={saving}>Cancelar</button>
                        {form.level === 'salesman' && !form.id && (
                            <button className="mc-btn" onClick={preVisualizar} disabled={saving || !!erro}>Pré-visualizar</button>
                        )}
                        <button className="mc-btn mc-btn-primary" onClick={salvar} disabled={saving || !!erro || lote}
                            title={lote ? 'Use Pré-visualizar para criar em lote' : ''}>
                            {saving ? 'Salvando…' : 'Salvar'}
                        </button>
                    </div>
                </div>
            )}

            <div className="mc-list">
                <h2>Exceções de loja e de vendedor {loading ? '(carregando…)' : `(${lista.length})`}</h2>
                {!subdomain && <p className="mc-empty">Selecione uma empresa.</p>}
                {subdomain && !loading && lista.length === 0 && <p className="mc-empty">Nenhuma exceção neste filtro.</p>}
                {lista.length > 0 && (
                    <div className="esc-tabela-rolagem">
                        <table className="mc-table">
                            <thead>
                                <tr><th>Nível</th><th>Alvo</th><th>Período</th><th>Tipo</th><th>Efeito</th><th>Horário</th><th>Situação</th><th>Motivo</th><th>Ativa</th><th></th></tr>
                            </thead>
                            <tbody>
                                {lista.map((e) => (
                                    <tr key={e.id}>
                                        <td>{e.level === 'store' ? 'Loja' : 'Vendedor'}</td>
                                        <td>{alvoTexto(e)}</td>
                                        <td>
                                            {brDate(e.date_from)}{e.date_to !== e.date_from ? ` a ${brDate(e.date_to)}` : ''}
                                            {(e.touches_closed_days || diaFechado(e.date_from, primeiroAberto)) && (
                                                <span className="esc-selo-fechado esc-selo-fechado-aviso" title="Toca dias fechados: editar ou remover vira uma revisão do passado, com motivo">
                                                    dia fechado
                                                </span>
                                            )}
                                        </td>
                                        <td>{KIND_LABEL[e.kind] || e.kind}</td>
                                        <td>{EFFECT_LABEL[e.effect] || e.effect}</td>
                                        <td>{horarioTexto(e)}</td>
                                        <td>{STATUS_LABEL[e.status] || e.status}</td>
                                        <td>{e.reason || '—'}</td>
                                        <td>{e.active ? 'Sim' : 'Não'}</td>
                                        <td className="mc-actions">
                                            <button className="mc-btn mc-btn-sm" onClick={() => abrirEdicao(e)}>✏️ Editar</button>
                                            <button className="mc-btn mc-btn-sm mc-btn-danger" onClick={() => pedirExclusao(e)}>🗑️ Remover</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <div className="mc-list esc-nacional">
                <h2>Feriados nacionais {carregandoNac ? '(carregando…)' : `(${nacionais.length})`}</h2>
                <p className="esc-dica">
                    Valem para <strong>todas as empresas</strong>. Fora de QA só quem está autorizado edita; se não for o seu
                    caso, a API recusa e mostra o motivo. Escala que “trabalha em feriado” (shopping) não folga no feriado.
                    Carnaval, Corpus Christi e feriados estaduais ou municipais não vêm no seed.
                    {primeiroAbertoNac && (
                        <> Datas anteriores a <strong>{brDate(primeiroAbertoNac)}</strong> estão fechadas e são imutáveis: valem para
                        todas as empresas, então o passado delas não pode mudar por aqui.</>
                    )}
                </p>
                <div className="mc-filters">
                    <div className="mc-field">
                        <label htmlFor="esc-nac-ano">Ano</label>
                        <input id="esc-nac-ano" type="number" min="2020" max="2100" value={ano} onChange={(e) => setAno(e.target.value)} />
                    </div>
                    <div className="mc-field mc-filters-actions esc-acoes">
                        <button className="mc-btn" onClick={simularSeed} disabled={ocupadoNac || !(Number(ano) >= 2020)}>Semear feriados de {ano}</button>
                        <button className="mc-btn mc-btn-primary" onClick={novoNac}>➕ Novo feriado</button>
                    </div>
                </div>

                {erroNac && <p className="esc-erro" role="alert">{erroNac}</p>}

                {formNac && (
                    <div className="mc-form-panel">
                        <h2>{formNac.id ? 'Editar feriado' : 'Novo feriado'}</h2>
                        <div className="mc-form-grid">
                            <div className="mc-field">
                                <label htmlFor="esc-nac-data">Data *</label>
                                <input id="esc-nac-data" type="date" value={formNac.date} onChange={(e) => setFormNac((p) => ({ ...p, date: e.target.value }))} />
                            </div>
                            <div className="mc-field">
                                <label htmlFor="esc-nac-nome">Nome *</label>
                                <input id="esc-nac-nome" type="text" value={formNac.label} onChange={(e) => setFormNac((p) => ({ ...p, label: e.target.value }))} />
                            </div>
                            <div className="mc-field">
                                <label htmlFor="esc-nac-tipo">Tipo</label>
                                <select id="esc-nac-tipo" value={formNac.kind} onChange={(e) => setFormNac((p) => ({ ...p, kind: e.target.value }))}>
                                    {KINDS.map((k) => <option key={k.key} value={k.key}>{k.label}</option>)}
                                </select>
                            </div>
                            <div className="mc-field">
                                <label htmlFor="esc-nac-efeito">Efeito</label>
                                <select id="esc-nac-efeito" value={formNac.effect} onChange={(e) => setFormNac((p) => ({ ...p, effect: e.target.value }))}>
                                    <option value="off">Folga</option>
                                    <option value="work">Trabalha</option>
                                </select>
                            </div>
                            <div className="mc-field">
                                <label htmlFor="esc-nac-regiao">Região (vazio = todo o país)</label>
                                <input id="esc-nac-regiao" type="text" maxLength={64} value={formNac.region} onChange={(e) => setFormNac((p) => ({ ...p, region: e.target.value }))} />
                                <small className="esc-dica">Vale para lojas com a mesma região (aba Calendário).</small>
                            </div>
                            <div className="mc-field">
                                <label><input type="checkbox" checked={formNac.active} onChange={(e) => setFormNac((p) => ({ ...p, active: e.target.checked }))} /> Ativo</label>
                            </div>
                        </div>
                        {erroFormNac && <p className="mc-nota">{erroFormNac}</p>}
                        <div className="mc-form-actions">
                            <button className="mc-btn" onClick={() => setFormNac(null)} disabled={ocupadoNac}>Cancelar</button>
                            <button className="mc-btn mc-btn-primary" onClick={salvarNac} disabled={ocupadoNac || !!erroFormNac}>
                                {ocupadoNac ? 'Salvando…' : 'Salvar'}
                            </button>
                        </div>
                    </div>
                )}

                {nacionais.length === 0 && !carregandoNac && <p className="mc-empty">Nenhum feriado cadastrado em {ano}.</p>}
                {nacionais.length > 0 && (
                    <div className="esc-tabela-rolagem">
                        <table className="mc-table">
                            <thead><tr><th>Data</th><th>Nome</th><th>Tipo</th><th>Efeito</th><th>Região</th><th>Ativo</th><th></th></tr></thead>
                            <tbody>
                                {nacionais.map((n) => (
                                    <tr key={n.id}>
                                        <td>{brDate(n.date)}</td>
                                        <td>{n.label}</td>
                                        <td>{KIND_LABEL[n.kind] || n.kind}</td>
                                        <td>{n.effect === 'off' ? 'Folga' : 'Trabalha'}</td>
                                        <td>{n.region || 'Todo o país'}</td>
                                        <td>{n.active ? 'Sim' : 'Não'}</td>
                                        <td className="mc-actions">
                                            {n.closed ? (
                                                <span className="esc-selo-fechado" title="Data fechada é imutável: não se edita, desativa nem remove. Para corrigir o passado de uma empresa, use uma exceção com motivo.">
                                                    fechado
                                                </span>
                                            ) : (
                                                <>
                                                    <button className="mc-btn mc-btn-sm" onClick={() => editarNac(n)}>✏️ Editar</button>
                                                    <button className="mc-btn mc-btn-sm mc-btn-danger" onClick={() => setNacParaExcluir(n)}>🗑️ Remover</button>
                                                </>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {paraExcluir && (
                <Dialogo titulo="Remover exceção" perigo rotuloConfirmar="Remover"
                    confirmarHabilitado={!exclusaoTocaFechado || motivoValido(motivoExcluir)}
                    onCancelar={() => setParaExcluir(null)} onConfirmar={excluir}>
                    <p>
                        Remover {KIND_LABEL[paraExcluir.kind] || paraExcluir.kind} de <strong>{alvoTexto(paraExcluir)}</strong>
                        {' '}({brDate(paraExcluir.date_from)}{paraExcluir.date_to !== paraExcluir.date_from ? ` a ${brDate(paraExcluir.date_to)}` : ''})?
                        Os dias voltam a seguir a escala.
                    </p>
                    {exclusaoTocaFechado && (
                        <>
                            <p className="dlg-efeito">
                                Esta exceção toca <strong>dias fechados</strong>. Remover vira uma revisão do passado: informe o motivo.
                                O estado anterior fica guardado na revisão anterior, e só quem está autorizado pode fazer isto.
                            </p>
                            <label htmlFor="esc-ex-motivo-rem">Motivo da remoção *</label>
                            <input id="esc-ex-motivo-rem" type="text" maxLength={240} value={motivoExcluir}
                                onChange={(e) => setMotivoExcluir(e.target.value)} />
                        </>
                    )}
                </Dialogo>
            )}

            {nacParaExcluir && (
                <Dialogo titulo="Remover feriado nacional" perigo rotuloConfirmar="Remover"
                    onCancelar={() => setNacParaExcluir(null)} onConfirmar={excluirNac}>
                    <p>Remover <strong>{nacParaExcluir.label}</strong> ({brDate(nacParaExcluir.date)}) para <strong>todas as empresas</strong>?</p>
                </Dialogo>
            )}

            {resumo && (
                <Dialogo titulo="Pré-visualização do lote" rotuloConfirmar={`Criar ${resumo.dados.length} exceç${resumo.dados.length === 1 ? 'ão' : 'ões'}`}
                    rotuloOcupado="Criando…" ocupado={saving} confirmarHabilitado={resumo.dados.length > 0}
                    onCancelar={() => setResumo(null)} onConfirmar={confirmarLote}>
                    <p>
                        {KIND_LABEL[resumo.payload.kind]} de {brDate(resumo.payload.date_from)}
                        {resumo.payload.date_to !== resumo.payload.date_from ? ` a ${brDate(resumo.payload.date_to)}` : ''},
                        efeito “{EFFECT_LABEL[resumo.payload.effect]}”, para {resumo.dados.length} vendedor(es).
                        {' '}{totalSemOverlap} sem conflito{resumo.dados.length - totalSemOverlap > 0
                            ? `; ${resumo.dados.length - totalSemOverlap} já têm exceção no período (vale a mais recente)` : ''}.
                    </p>
                    {(resumo.exigeAutorDoPassado || resumo.dados.some((d) => Number(d.closed_days) > 0)) && (
                        <p className="esc-aviso" role="note">
                            Dia fechado: {resumo.dados.reduce((n, d) => n + (Number(d.closed_days) || 0), 0)} dia(s) já fechado(s) serão
                            revisados, com o motivo informado.{resumo.exigeAutorDoPassado ? ' Só quem está autorizado a corrigir o passado pode confirmar.' : ''}
                        </p>
                    )}
                    <div className="esc-resumo-lista">
                        {resumo.dados.map((d) => (
                            <div key={d.salesman_id || d.store_id} className="esc-resumo-item">
                                <strong>{d.salesman_name || `Vendedor ${d.salesman_id || d.store_id}`}</strong>
                                {Number(d.closed_days) > 0 && (
                                    <small className="esc-resumo-aviso">{d.closed_days} dia(s) fechado(s) serão revisados</small>
                                )}
                                {(d.overlaps || []).length > 0 && (
                                    <small className="esc-resumo-aviso">
                                        já tem: {d.overlaps.map((o) => `${KIND_LABEL[o.kind] || o.kind} ${brDate(o.date_from)}–${brDate(o.date_to)}`).join('; ')}
                                    </small>
                                )}
                            </div>
                        ))}
                    </div>
                </Dialogo>
            )}

            {seed && (
                <Dialogo titulo={`Semear feriados de ${seed.ano}`} rotuloConfirmar="Semear" rotuloOcupado="Semeando…" ocupado={ocupadoNac}
                    confirmarHabilitado={seed.dados.created.length > 0}
                    onCancelar={() => setSeed(null)} onConfirmar={aplicarSeed}>
                    {seed.dados.created.length === 0
                        ? <p>Todos os {seed.dados.already_present} feriados nacionais de {seed.ano} já estão cadastrados.</p>
                        : (
                            <>
                                <p>Serão criados {seed.dados.created.length} feriado(s) nacionais por lei ({seed.dados.already_present} já existem):</p>
                                {Array.isArray(seed.dados.ignored_closed) && seed.dados.ignored_closed.length > 0 && (
                                    <p className="esc-aviso">{seed.dados.ignored_closed.length} data(s) já fechada(s) serão ignoradas: {seed.dados.ignored_closed.map((x) => brDate(x.date || x)).join(', ')}.</p>
                                )}
                                <ul className="esc-lista-heranca">
                                    {seed.dados.created.map((c) => <li key={c.date}>{brDate(c.date)} — {c.label}</li>)}
                                </ul>
                            </>
                        )}
                </Dialogo>
            )}
        </div>
    );
}

export default ExcecoesTab;
